import { Box, CircularProgress } from '@mui/material';
import BackendService from 'BackendService';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import ArticleCard from '../../components/ArticleCard';
import { Article, ArticleContent } from '../../models/Article';
import {
  ENABLE_MOCK_ARTICLES,
  getMockArticleContent,
  getMockPendingPreviewArticles,
} from '../../utils/mockArticles';
import { StyledButton, StyledButtonGroup } from '../styles/StyledButtons';
import StyledSection from '../styles/StyledSection';
import { StyledHeading, StyledTypography } from '../styles/styles';
import Pagination, { PaginationData } from '../utils/Pagination';

interface ArticlesForApprovalComponentState {
  loading: boolean;
  contents?: { article: Article; content: ArticleContent }[];
  pagination?: PaginationData;
  currentPage: number;
}

class ArticlesForApproval extends React.Component<
  { navigate: ReturnType<typeof useNavigate> },
  ArticlesForApprovalComponentState
> {
  private pollingInterval: NodeJS.Timeout | null = null;

  constructor(props: { navigate: ReturnType<typeof useNavigate> }) {
    super(props);
    this.state = {
      loading: true,
      currentPage: 1,
    };
  }

  loadContents(page: number = 1) {
    // Use mock data if enabled, otherwise use real backend service
    if (ENABLE_MOCK_ARTICLES) {
      this.loadMockContents(page);
      return;
    }

    const backendService = new BackendService();

    backendService
      .getPendingPreviewArticles(page)
      .then((response: { data: Article[]; pagination: PaginationData }) => {
        return Promise.all(
          response.data.map(async (article) => {
            const content = await backendService.getArticleContent(article);

            return {
              article,
              content,
            };
          }),
        ).then((contents: { article: Article; content: ArticleContent }[]) => {
          this.setState({
            contents,
            pagination: response.pagination,
            currentPage: page,
            loading: false,
          });
        });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  }

  loadMockContents(page: number = 1) {
    getMockPendingPreviewArticles(page)
      .then((response: { data: Article[]; pagination: PaginationData }) => {
        return Promise.all(
          response.data.map(async (article) => {
            const content = await getMockArticleContent(article);

            return {
              article,
              content,
            };
          }),
        ).then((contents: { article: Article; content: ArticleContent }[]) => {
          this.setState({
            contents,
            pagination: response.pagination,
            currentPage: page,
            loading: false,
          });
        });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  }

  componentDidMount(): void {
    this.loadContents(this.state.currentPage);

    this.pollingInterval = setInterval(() => {
      this.loadContents(this.state.currentPage);
    }, 10000); // every 10 seconds
  }

  componentWillUnmount(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }

  handlePageChange = (newPage: number) => {
    this.setState({ loading: true });
    this.loadContents(newPage);
  };

  renderArticlesForApproval() {
    const { loading, contents } = this.state;

    if (loading) {
      return (
        <Box sx={{ pt: 5, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!contents) {
      return (
        <Box sx={{ pt: 5, textAlign: 'center' }}>
          <StyledTypography>Failed to load article content.</StyledTypography>
        </Box>
      );
    }

    if (!contents.length) {
      return (
        <Box sx={{ pt: 5, textAlign: 'center' }}>
          <StyledTypography>No articles need approval at the moment</StyledTypography>
        </Box>
      );
    }

    return (
      <Box sx={{ px: 3, pb: 5 }}>
        <StyledHeading>
          Articles Requiring Approval
          {ENABLE_MOCK_ARTICLES && (
            <Box
              component="span"
              sx={{
                ml: 2,
                px: 1,
                py: 0.5,
                bgcolor: 'warning.main',
                color: 'warning.contrastText',
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 'bold',
              }}
            >
              MOCK DATA
            </Box>
          )}
        </StyledHeading>
        <StyledSection tone="tone1" mb="4" additionalClassName="content-management-container">
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              width: '100%',
            }}
          >
            {this.state.contents?.map(({ article, content }) => {
              return (
                <Box
                  key={article.id}
                  sx={{
                    flex: '0 0 auto',
                    minWidth: { xs: '100%', sm: '300px', md: '350px' },
                    maxWidth: { xs: '100%', sm: '400px', md: '450px' },
                  }}
                >
                  <ArticleCard tone="tone1" content={content} linkTo={`/articles/${article.id}`} />
                </Box>
              );
            })}
          </Box>
        </StyledSection>

        {/* Pagination */}
        {this.state.pagination && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              pagination={this.state.pagination}
              onPageChange={this.handlePageChange}
              loading={this.state.loading}
            />
          </Box>
        )}
      </Box>
    );
  }

  render() {
    return (
      <>
        <StyledButtonGroup justifyContent="flex-end">
          <StyledButton onClick={() => this.props.navigate('/allArticles')}>
            View All Articles
          </StyledButton>
        </StyledButtonGroup>
        {this.renderArticlesForApproval()}
      </>
    );
  }
}

const ArticlesForApprovalWithNavigation = () => {
  const navigate = useNavigate();

  return <ArticlesForApproval navigate={navigate} />;
};

export default ArticlesForApprovalWithNavigation;
